import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Tooltip,
  Avatar,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Science as ObservatoryIcon,
  Timeline as TimelineIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz';

import { apiService } from '../../services/apiService';
import { websocketService } from '../../services/websocketService';
import { useAuth } from '../../contexts/AuthContext';
import { getAdministrableObservatories, hasObservatoryAdminCapabilities } from '../../utils/userCapabilities';

function MyObservatories() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [observatories, setObservatories] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();

    // Set up real-time updates
    websocketService.onObservatoryUpdate(handleObservatoryUpdate);

    // Unsubscribe on unmount — the listener array lives on the singleton
    // websocketService, so without this each remount stacks another handler.
    return () => {
      websocketService.offObservatoryUpdate(handleObservatoryUpdate);
    };
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load user memberships and all observatories in parallel
      const [membershipsResponse, observatoriesResponse] = await Promise.all([
        apiService.getUserMemberships(user?.id || user?._id),
        apiService.getObservatories()
      ]);

      const userMemberships = membershipsResponse?.success ? (membershipsResponse.data || []) : [];
      const allObservatories = observatoriesResponse.data?.observatories || observatoriesResponse.data || [];
      
      setMemberships(userMemberships);

      // Filter observatories to only include those where user has admin capabilities
      const adminObservatoryIds = getAdministrableObservatories(userMemberships).map(membership => 
        membership.observatory_id || membership.observatory?._id
      );
      
      const adminObservatories = allObservatories.filter(observatory => 
        adminObservatoryIds.includes(observatory._id)
      );

      setObservatories(adminObservatories);
      
      console.log('User memberships:', userMemberships);
      console.log('Administrable observatories:', adminObservatories);
    } catch (error) {
      console.error('Error loading data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading observatories',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleObservatoryUpdate = (update) => {
    // Update in-place without showing loading spinner to avoid blinking
    setObservatories(prev => prev.map(obs =>
      obs._id === update._id ? { ...obs, ...update } : obs
    ));
  };

  const handleToggleDispatch = async (observatoryId, currentStatus) => {
    try {
      await apiService.toggleObservatoryDispatch(observatoryId, !currentStatus);
      setSnackbar({
        open: true,
        message: `Dispatching ${!currentStatus ? 'enabled' : 'disabled'} successfully`,
        severity: 'success'
      });
      loadData();
    } catch (error) {
      console.error('Error toggling dispatch status:', error);
      setSnackbar({
        open: true,
        message: 'Error updating dispatch status',
        severity: 'error'
      });
    }
  };

  const handleToggleAutomation = async (observatoryId, currentStatus) => {
    try {
      await apiService.toggleObservatoryAutomation(observatoryId, !currentStatus);
      setSnackbar({
        open: true,
        message: `Automation ${!currentStatus ? 'enabled' : 'disabled'} successfully`,
        severity: 'success'
      });
      loadData();
    } catch (error) {
      console.error('Error toggling automation status:', error);
      setSnackbar({
        open: true,
        message: 'Error updating automation status',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'busy':
        return 'warning';
      case 'offline':
        return 'error';
      case 'maintenance':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">
            My Observatories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Observatories where you have administrative privileges
          </Typography>
        </Box>
        <Box>
          {(user?.role === 'server_admin' || hasObservatoryAdminCapabilities(memberships)) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/observatories')}
              sx={{ mr: 2 }}
            >
              Create Observatory
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {observatories.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ObservatoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Administrative Observatories Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You don't have administrative privileges on any observatories yet.
                Contact the observatory owner to request administrative access.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Observatory</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Dispatching</TableCell>
                    <TableCell>Automation</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Authentication</TableCell>
                    <TableCell>Last Heartbeat</TableCell>
                    <TableCell>API Key</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {observatories.map((observatory) => (
                    <TableRow 
                      key={observatory._id}
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <TableCell onClick={() => navigate(`/admin/observatories/${observatory._id}?from=my-observatories`)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: 'primary.main' }}>
                            <ObservatoryIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ 
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              }
                            }}>
                              {observatory.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {observatory.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/admin/observatories/${observatory._id}?from=my-observatories`)}>
                        {observatory.owner ? (
                          <Box>
                            <Typography variant="body2">
                              {observatory.owner.name}
                            </Typography>
                            {observatory.owner.email && (
                              <Typography variant="caption" color="text.secondary">
                                {observatory.owner.email}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No owner
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={() => navigate(`/admin/observatories/${observatory._id}?from=my-observatories`)}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={observatory.status}
                            color={getStatusColor(observatory.status)}
                            size="small"
                          />
                          {observatory.dispatch_enabled === false && (
                            <Chip
                              label="Dispatch Disabled"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {(observatory.plugin_options?.operations_enabled === false || observatory.system_status?.operations_enabled === false) && (
                            <Chip
                              label="Ops Disabled"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={observatory.dispatch_enabled !== false}
                              onChange={() => handleToggleDispatch(observatory._id, observatory.dispatch_enabled !== false)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" color={observatory.dispatch_enabled !== false ? 'success.main' : 'text.disabled'}>
                              {observatory.dispatch_enabled !== false ? 'Enabled' : 'Disabled'}
                            </Typography>
                          }
                        />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={observatory.automation_enabled !== false}
                              onChange={() => handleToggleAutomation(observatory._id, observatory.automation_enabled !== false)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" color={observatory.automation_enabled !== false ? 'success.main' : 'text.disabled'}>
                              {observatory.automation_enabled !== false ? 'Enabled' : 'Disabled'}
                            </Typography>
                          }
                        />
                      </TableCell>
                      <TableCell onClick={() => navigate(`/admin/observatories/${observatory._id}?from=my-observatories`)}>
                        <Typography variant="body2">
                          {[observatory.location?.city, observatory.location?.country].filter(Boolean).join(', ') || ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {observatory.location?.latitude?.toFixed(2)}°{observatory.location?.latitude >= 0 ? 'N' : 'S'}, {Math.abs(observatory.location?.longitude || 0).toFixed(2)}°{observatory.location?.longitude >= 0 ? 'E' : 'W'} • {observatory.location?.altitude_meters || 0}m
                        </Typography>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/admin/observatories/${observatory._id}?from=my-observatories`)}>
                        {observatory.api_key_created_at || observatory.current_session?.start_time ? (
                          <Box>
                            {observatory.api_key_created_at && (
                              <Typography variant="caption" color="text.secondary">
                                API Key: {formatInTimeZone(new Date(observatory.api_key_created_at), 'UTC', 'MMM dd, HH:mm')}
                              </Typography>
                            )}
                            {observatory.current_session?.start_time && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Session: {formatInTimeZone(new Date(observatory.current_session.start_time), 'UTC', 'MMM dd, HH:mm')}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No auth data
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={() => navigate(`/admin/observatories/${observatory._id}?from=my-observatories`)}>
                        {observatory.last_heartbeat
                          ? `${formatInTimeZone(new Date(observatory.last_heartbeat), 'UTC', 'MMM dd, HH:mm')} UTC`
                          : 'Never'}
                      </TableCell>
                      <TableCell onClick={() => navigate(`/admin/observatories/${observatory._id}?from=my-observatories`)}>
                        {observatory.api_key_hash ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label="Active"
                              color="success"
                              size="small"
                            />
                            {observatory.api_key_type && (
                              <Chip
                                label={
                                  observatory.api_key_type === 'temporary' ? 'Temp' : 
                                  observatory.api_key_type === 'extended' ? 'Ext' : 
                                  observatory.api_key_type === 'legacy' ? 'Legacy' :
                                  'Multi'
                                }
                                variant="outlined"
                                size="small"
                                color="info"
                                sx={{ fontSize: '0.6rem', height: '20px' }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Chip
                            label="None"
                            color="default"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View Timeline">
                          <IconButton onClick={() => navigate(`/timeline/${observatory._id}`)} size="small">
                            <TimelineIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Observatory Settings">
                          <IconButton onClick={() => navigate(`/admin/observatories/${observatory._id}?from=my-observatories`)} size="small">
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MyObservatories;